import {
    createApi,
    fetchBaseQuery,
    retry
  } from '@reduxjs/toolkit/query/react'
  import {baseQueryWithRetry} from "@/services/rtk-query/base-query";
  
  /**
   * Create a base API to inject endpoints into elsewhere.
   * Components using this API should import from the injected site,
   * in order to get the appropriate types,
   * and to ensure that the file injecting the endpoints is loaded
   */
  export const mainApi: any = createApi({
    /**
     * `reducerPath` is optional and will not be required by most users.
     * This is useful if you have multiple API definitions,
     * e.g. where each has a different domain, with no interaction between endpoints.
     * Otherwise, a single API definition should be used in order to support tag invalidation,
     * among other services
     */
    reducerPath: 'mainApi',
    /**
     * A bare bones base query would just be `baseQuery: fetchBaseQuery({ baseUrl: '/' })`
     */
    baseQuery: baseQueryWithRetry,
    /**
     * Tag types must be defined in the original API definition
     * for any tags that would be provided by injected endpoints
     */
    tagTypes: [
      'User',
    ],
    /**
     * This api has endpoints injected in adjacent files,
     * which is why no endpoints are shown below.
     * If you want all endpoints defined in the same file, they could be included here instead
     */
    endpoints: () => ({}),
  });
  
  export const enhancedApi = mainApi.injectEndpoints({
    endpoints: (builder: any) => ({
      getNotificationsCount: builder.query({
        query: (url: string) => url,
        transformResponse: (response: { data: object }) => response.data,
      }),
      getUser: builder.query({
        query: (id: string) => id,
        transformResponse: (response: { data: object }) => response.data,
        providesTags: ["User"],
        keepUnusedDataFor: 0,
        onQueryStarted: async (
          _arg: string,
          { dispatch, queryFulfilled }: { dispatch: any; queryFulfilled: Promise<any> }
        ): Promise<void> => {
          try {
            await queryFulfilled;
          } catch (error) {
            console.error("Error invalidating dashboard tags:", error);
          }
        },
      }),
      uploadFile: builder.mutation({
        query: (fileData: any) => ({
          url: "/upload/file",
          method: "POST",
          body: fileData,
          formData: true,
        }),
      }),
    }),
  });
  
  export const {
    useGetNotificationsCountQuery,
    useGetUserQuery,
    useLazyGetNotificationsCountQuery,
    useLazyGetUserQuery,
    useUploadFileMutation,
  } = enhancedApi;
  
  